'use es6';

import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UITruncateString from 'UIComponents/text/UITruncateString';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import MobilePanel from 'ecosystem-marketplace-core/js/components/mobile/MobilePanel';
import MobilePanelBody from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelBody';
import MobilePanelHeader from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelHeader';
import MobilePanelNav from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelNav';
import MobilePanelNavItem from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelNavItem';
import * as GROUPED_CATEGORIES from 'ecosystem-marketplace-core/constants/groupTags/SuperCategoryGroup';
import { SUPER_CATEGORY_ORDER } from 'marketplace-ui-apps-core/common/constants/groupTags/SuperCategoryOrder';
import { getSuperCategoryI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { useFocusTrap } from 'marketplace-ui-common/hooks/useFocusTrap';
import { getCategoryI18nKey } from 'marketplace-ui-common/utils/i18n';
import StorefrontFilters from '../../../../records/StorefrontFilters';
import * as FilterKey from '../../../../constants/search/FilterKey';
import MobileCategorySelect from './MobileCategorySelect';
import MobileFilterNavItemWrapper from './MobileFilterNavItemWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledHeaderAction = styled(UIButton).withConfig({
  displayName: "MobileNavigationPanel__StyledHeaderAction",
  componentId: "nu0vxf-0"
})(["&&&&{padding:4px;}"]);
const ALL_CATEGORIES = 'ALL_CATEGORIES';
const textPath = 'appMarketplace.storefront.mobileNavigation';
const translateCategory = (superCategory, category) => {
  if (category) {
    return unescapedText(getCategoryI18nKey(category));
  }
  return unescapedText(`${textPath}.fullSuperCategory`, {
    superCategory: unescapedText(getSuperCategoryI18nKey(superCategory))
  });
};
const MobileNavigationPanel = ({
  filters,
  onCategoryChange,
  onPanelClose,
  open
}) => {
  const [activePanelIds, setActivePanelIds] = useState([]);
  const [activeCategory, setActiveCategory] = useState(filters.get(FilterKey.CATEGORY) ? filters.get(FilterKey.CATEGORY) : null);
  const [activeSuperCategory, setActiveSuperCategory] = useState(filters.get(FilterKey.SUPER_CATEGORY));
  const mobileNavPanelRef = useRef(null);
  useEffect(() => {
    if (open) {
      mobileNavPanelRef.current.focus();
    }
  }, [open]);
  const clickFilter = useCallback(value => {
    if (activePanelIds && activePanelIds.indexOf(value) === -1) {
      setActivePanelIds(panelIds => [...panelIds, value]);
    }
  }, [activePanelIds, setActivePanelIds]);
  const closePanel = useCallback(value => {
    setActivePanelIds(activePanelIds.filter(panelId => panelId !== value));
  }, [activePanelIds]);
  const handleClose = useCallback(() => {
    onPanelClose();
  }, [onPanelClose]);
  const applyFilters = useCallback((isAllCategories = false) => {
    if (isAllCategories) {
      onCategoryChange(undefined);
    } else {
      onCategoryChange(activeSuperCategory, activeCategory);
    }
    handleClose();
  }, [activeCategory, activeSuperCategory, handleClose, onCategoryChange]);
  const handleAllCategoriesClick = useCallback(() => {
    setActiveSuperCategory(ALL_CATEGORIES);
    setActiveCategory(null);
    applyFilters(true);
  }, [applyFilters, setActiveCategory, setActiveSuperCategory]);
  const getPanelButtonText = useCallback((superCategory, category) => {
    return /*#__PURE__*/_jsxs(UITruncateString, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getSuperCategoryI18nKey(superCategory)
      }), /*#__PURE__*/_jsx("br", {}), activeSuperCategory === superCategory && /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: translateCategory(superCategory, category)
      })]
    });
  }, [activeSuperCategory]);
  useFocusTrap(open);
  return open && /*#__PURE__*/_jsxs(MobilePanel, {
    align: 'bottom',
    open: open,
    children: [/*#__PURE__*/_jsx(MobilePanelHeader, {
      itemLeft: /*#__PURE__*/_jsx(StyledHeaderAction, {
        buttonRef: mobileNavPanelRef,
        className: "m-left-2",
        onClick: handleClose,
        use: "transparent",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.cancel`
        })
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.browse`
      })
    }), /*#__PURE__*/_jsx(MobilePanelBody, {
      children: /*#__PURE__*/_jsxs(MobilePanelNav, {
        title: unescapedText(`${textPath}.categories`),
        children: [/*#__PURE__*/_jsx(MobilePanelNavItem, {
          itemId: ALL_CATEGORIES,
          onClickItem: handleAllCategoriesClick,
          title: unescapedText('appMarketplace.storefront.filters.category.items.all.titleText')
        }, ALL_CATEGORIES), SUPER_CATEGORY_ORDER.map(superCategory => /*#__PURE__*/_jsx(MobileFilterNavItemWrapper, {
          applyFilters: () => applyFilters(),
          clickFilter: clickFilter,
          filterKey: superCategory,
          onPanelClose: () => {
            closePanel(superCategory);
            mobileNavPanelRef.current.focus();
          },
          searchEnabled: true,
          open: activePanelIds && activePanelIds.indexOf(superCategory) !== -1,
          panelTitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getSuperCategoryI18nKey(superCategory)
          }),
          textSection: getPanelButtonText(superCategory, activeCategory),
          children: /*#__PURE__*/_jsx(MobileCategorySelect, {
            defaultValue: unescapedText(`${textPath}.fullSuperCategory`, {
              superCategory: unescapedText(getSuperCategoryI18nKey(superCategory))
            }),
            value: activeCategory,
            options: GROUPED_CATEGORIES[superCategory],
            translateOption: category => translateCategory(superCategory, category),
            searchPlaceholder: unescapedText(`${textPath}.search`),
            onChange: value => {
              setActiveCategory(value);
              setActiveSuperCategory(superCategory);
            },
            superCategory: superCategory,
            activeSuperCategory: activeSuperCategory
          })
        }, superCategory))]
      })
    })]
  });
};
MobileNavigationPanel.propTypes = {
  filters: PropTypes.instanceOf(StorefrontFilters).isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onPanelClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};
export default MobileNavigationPanel;